import { ConnectionTestInvitationToken } from "@repo/shared-types/sso";
import { type ReactNode, useEffect, useState } from "react";
import { z } from "zod";
import { Button, ErrorText } from "../../shared/web/ui/index.ts";

export function ConnectionTest(): ReactNode {
  const [token] = useState<string>(() => window.location.hash.slice(1));
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const result = new URLSearchParams(window.location.search).get("result");
  const reference = z.string().uuid().safeParse(new URLSearchParams(window.location.search).get("reference"));
  const [startReference, setStartReference] = useState<string | null>(null);
  useEffect(() => {
    // Keep the invitation out of later history entries and copied result URLs.
    if (window.location.hash) window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
    const openReplacement = (): void => {
      // Pasting a replacement link can be a fragment-only navigation. A fresh document
      // discards the old token, errors and pending request; ordinary anchors remain usable.
      if (ConnectionTestInvitationToken.safeParse(window.location.hash.slice(1)).success) window.location.reload();
    };
    window.addEventListener("hashchange", openReplacement);
    return () => window.removeEventListener("hashchange", openReplacement);
  }, []);
  async function start(): Promise<void> {
    setPending(true);
    setError("");
    try {
      const response = await fetch("/api/auth/connection-test", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const body: unknown = await response.json();
      if (typeof body === "object" && body !== null && "testId" in body) {
        const parsed = z.string().uuid().safeParse(body.testId);
        if (parsed.success) setStartReference(parsed.data);
      }
      if (!response.ok) {
        setError(
          response.status === 400
            ? "This test link is no longer available. It may have been used, expired, or replaced by a connection change. Ask your Portal contact for a new link; do not retry this one."
            : "The Portal could not start the test. Ask your Portal contact to check the saved result before sending you another link.",
        );
        setPending(false);
        return;
      }
      if (typeof body !== "object" || body === null || !("url" in body) || typeof body.url !== "string") throw new Error("Test unavailable");
      window.location.assign(body.url);
    } catch {
      setError("The Portal could not be reached or returned an unexpected response. Tell your Portal contact before trying again; this one-use link may already have been used.");
      setPending(false);
    }
  }
  return (
    <section className="stack measure" aria-labelledby="connection-test-title">
      <p className="t-caption">Linear Portal</p>
      <h1 id="connection-test-title" className="t-display">
        Company sign-in test
      </h1>
      {token ? (
        <>
          <p>Use your assigned company account on a company-approved device. After signing in, you will return here to see the result.</p>
          <p>Once you continue, finish within five minutes in this browser. This link works once.</p>
          <p className="t-caption">This checks company sign-in only. It does not create a Portal account or open the Helpdesk.</p>
          <div>
            <Button variant="primary" disabled={pending || !!error} onClick={() => void start()}>
              {pending ? "Opening company sign-in…" : "Continue with company account"}
            </Button>
          </div>
          {error && <ErrorText>{error}</ErrorText>}
        </>
      ) : result === "success" ? (
        <>
          <h2 className="t-title">Sign-in test completed</h2>
          <p>Your company identity and requester access were verified. Your Portal contact can review the saved result and finish enabling the Helpdesk. You can close this tab.</p>
        </>
      ) : result === "failed" ? (
        <>
          <h2 className="t-title">Sign-in could not be verified</h2>
          <p>
            Your Portal contact can review the test. Share {reference.success ? "the test reference below" : "the time of your attempt"} with them and wait for their next step. Do not reuse the
            original link.
          </p>
        </>
      ) : (
        <>
          <h2 className="t-title">Test link incomplete</h2>
          <p>
            Open the original complete link from your Portal contact. A link copied from this page's address bar no longer contains the invitation. If you already started sign-in, ask your contact to
            review the result before sending a new link.
          </p>
        </>
      )}
      {(reference.success || startReference) && <p className="t-caption">Test reference: {reference.success ? reference.data : startReference}</p>}
    </section>
  );
}
